import { useAtomValue } from 'jotai';
import React, { Suspense } from 'react';
import { Flex, Spinner } from '@optiaxiom/react';

import { opalAuthAtom } from '@frontend/atoms';
import { useOpalUser } from '@frontend/hooks';

export type MarkdownTextProps = {
  text: string;
};

// @ts-expect-error - External module import
type ArtifactProps = import('OpalChat/Artifact').ArtifactProps;

// @ts-expect-error - External module import
type ArtifactContainerProps = import('OpalChat/Artifact').ArtifactContainerProps;

// @ts-expect-error - External module import
type ArtifactProviderProps = import('OpalChat/Artifact').ArtifactProviderProps;

const FallBackChildrenRenderer: React.FC<ArtifactContainerProps> = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return children;
};

const FallBackArtifacts: React.FC<MarkdownTextProps> = () => <div>Artifacts failed to load</div>;

// Create a lazy-loaded component using React.lazy()
const LazyArtifactContainer = React.lazy<React.ComponentType<ArtifactContainerProps>>(() =>
  // @ts-expect-error - External module import
  import('OpalChat/Artifact')
    .then(m => ({
      default: m.ArtifactContainer,
    }))
    .catch(error => {
      console.error('Failed to load OpalChat ArtifactContainer component:', error);
      return {
        default: FallBackChildrenRenderer,
      };
    })
);

// Create a lazy-loaded component using React.lazy()
const LazyArtifactProvider = React.lazy<React.ComponentType<ArtifactProviderProps>>(() =>
  // @ts-expect-error - External module import
  import('OpalChat/Artifact')
    .then(m => ({
      default: m.ArtifactProvider,
    }))
    .catch(error => {
      console.error('Failed to load OpalChat ArtifactContainer component:', error);
      return {
        default: FallBackChildrenRenderer,
      };
    })
);

// Create a lazy-loaded component using React.lazy()
const LazyArtifacts = React.lazy<React.ComponentType<ArtifactProps>>(() =>
  // @ts-expect-error - External module import
  import('OpalChat/Artifact')
    .then(m => ({
      default: m.Artifacts,
    }))
    .catch(error => {
      console.error('Failed to load OpalChat ArtifactContainer component:', error);
      return {
        default: FallBackArtifacts,
      };
    })
);

// Simple loader component for markdown text
const ComponentLoader: React.FC = () => (
  <Flex justifyContent="center" alignItems="center" p="8">
    <Spinner size="sm" />
  </Flex>
);

const ArtifactProviderWithAuth: React.FC<ArtifactProviderProps> = props => {
  return (
    <Suspense fallback={<ComponentLoader />}>
      <LazyArtifactProvider {...props} />
    </Suspense>
  );
};

export const ArtifactProvider: React.FC<{ children: JSX.Element }> = ({ children }) => {
  const opalAuth = useAtomValue(opalAuthAtom);
  const { opalUser } = useOpalUser();
  return (
    <ArtifactProviderWithAuth
      currentUser={{
        instanceId: opalUser.instance?.instance_id,
        avatarUrl: '',
        name: opalUser.firstname + ' ' + opalUser.lastname,
      }}
      opalAuth={opalAuth}
    >
      {children}
    </ArtifactProviderWithAuth>
  );
};

export const ArtifactContainer: React.FC<ArtifactContainerProps> = props => {
  return (
    <Suspense fallback={<ComponentLoader />}>
      <LazyArtifactContainer {...props} />
    </Suspense>
  );
};

export const Artifacts: React.FC<ArtifactProps> = props => {
  return (
    <Suspense fallback={<ComponentLoader />}>
      <LazyArtifacts {...props} />
    </Suspense>
  );
};
